import React, { useState, ChangeEvent } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../types/admin';

interface FilterOption {
  value: string;
  label: string;
}

interface AdminFilterBarProps {
  module: string;
  brand: BrandConfig;
  Icons: IconsType;
}

const AdminFilterBar: React.FC<AdminFilterBarProps> = ({ module, brand, Icons }) => {
  const [showFilters, setShowFilters] = useState(true);

  const [filters, setFilters] = useState<Record<string, string>>({
    office: '-1',
    operator: '-1',
    fulfilledBy: '-1',
    account: '-1',
    type: '-1'
  });

  // Sample data for dropdowns
  const offices: FilterOption[] = [
    { value: '-1', label: 'Select Office' },
    { value: '48', label: brand.company.name }
  ];

  const employees: FilterOption[] = [
    { value: '-1', label: 'Select Employee' },
    { value: '89183', label: 'System Administrator' },
    { value: '89092', label: 'Walter Lee' }
  ];

  const agents: FilterOption[] = [
    { value: '-1|-1', label: 'Select Agent' },
    { value: '99936|-1', label: 'County Process Service, Inc.' },
    { value: '41092|-1', label: 'George Sano' },
    { value: '148522|-1', label: 'Legal Lawgic' },
  ];

  const accounts: FilterOption[] = [
    { value: '-1', label: 'Select Account' }
  ];

  const types: FilterOption[] = [
    { value: '-1', label: 'All' },
    { value: '0', label: 'Electronic' },
    { value: '1', label: 'Physical' }
  ];

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const selectClass = `
    appearance-none px-3 py-2 pr-8 text-sm rounded-lg border
    focus:outline-hidden focus:ring-2 focus:ring-offset-0
  `;

  return (
    <div className="px-4 py-3" style={{ backgroundColor: brand.colors.bgLight }}>
      <div className="flex items-center justify-between mb-3">
        <button
          className="text-sm font-medium hover:underline"
          onClick={() => setShowFilters(!showFilters)}
          style={{ color: brand.colors.link }}
        >
          {showFilters ? 'Hide' : 'Show'} Filters
        </button>
      </div>

      {showFilters && (
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="filter-office" className="text-sm" style={{ color: brand.colors.textLight }}>Office:</label>
            <div className="relative">
              <select
                id="filter-office"
                value={filters.office}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => handleFilterChange('office', e.target.value)}
                className={selectClass}
                style={{
                  borderColor: brand.colors.border,
                  color: brand.colors.text
                }}
              >
                {offices.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <Icons.ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: brand.colors.textLight }} />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="filter-operator" className="text-sm" style={{ color: brand.colors.textLight }}>Operator:</label>
            <div className="relative">
              <select
                id="filter-operator"
                value={filters.operator}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => handleFilterChange('operator', e.target.value)}
                className={selectClass}
                style={{
                  borderColor: brand.colors.border,
                  color: brand.colors.text
                }}
              >
                {employees.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <Icons.ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: brand.colors.textLight }} />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="filter-fulfilled-by" className="text-sm" style={{ color: brand.colors.textLight }}>Fulfilled by:</label>
            <div className="relative">
              <select
                id="filter-fulfilled-by"
                value={filters.fulfilledBy}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => handleFilterChange('fulfilledBy', e.target.value)}
                className={selectClass}
                style={{
                  borderColor: brand.colors.border,
                  color: brand.colors.text
                }}
              >
                {agents.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <Icons.ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: brand.colors.textLight }} />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="filter-account" className="text-sm" style={{ color: brand.colors.textLight }}>Account:</label>
            <div className="relative">
              <select
                id="filter-account"
                value={filters.account}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => handleFilterChange('account', e.target.value)}
                className={selectClass}
                style={{
                  borderColor: brand.colors.border,
                  color: brand.colors.text
                }}
              >
                {accounts.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <Icons.ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: brand.colors.textLight }} />
            </div>
          </div>

          {module === 'orders' && (
            <div className="flex items-center gap-2">
              <label htmlFor="filter-type" className="text-sm" style={{ color: brand.colors.textLight }}>Type:</label>
              <div className="relative">
                <select
                  id="filter-type"
                  value={filters.type}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => handleFilterChange('type', e.target.value)}
                  className={selectClass}
                  style={{
                    borderColor: brand.colors.border,
                    color: brand.colors.text
                  }}
                >
                  {types.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <Icons.ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: brand.colors.textLight }} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminFilterBar;
